/**
 * Synthetic notification chime via the Web Audio API.
 *
 * Why synthetic instead of an asset MP3:
 *   - No network round-trip on first play.
 *   - No second decoder buffer to manage.
 *   - The chime is short (~250 ms) and the tone shape is fine-tuned
 *     for "notification" recognizability without sounding intrusive.
 *
 * Autoplay policy: modern browsers (Chrome 66+, Safari 14+, Firefox)
 * refuse to play audio until the page has received a user gesture.
 * The lazy-init pattern below creates the AudioContext on the FIRST
 * call - which always happens after a user interaction (clicking the
 * bell or any other admin action) so the context is usable. If the
 * context is still 'suspended' on the first chime, we resume it
 * before playing.
 *
 * Throttle: rapid-fire notifications (5 in 1 s) collapse into one
 * chime. The audio is purely an alert, not a record - the bell badge
 * is the truth.
 */

const STORAGE_KEY = 'utazzo.notifications.sound';
const THROTTLE_MS = 600;

let audioCtx: AudioContext | null = null;
let lastPlayedAt = 0;

const getCtx = (): AudioContext | null => {
  if (typeof window === 'undefined') return null;
  if (audioCtx) return audioCtx;
  const AC =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  try {
    audioCtx = new AC();
    return audioCtx;
  } catch {
    return null;
  }
};

/**
 * Two-tone chime. Higher pitch first (988 Hz, B5) for ~120 ms, then
 * lower (740 Hz, F#5) for ~180 ms. The slight overlap + quick decay
 * envelope keeps it pleasant. Volume capped at 0.18 of max - audible
 * over typical office ambience without making the operator flinch.
 */
export const playNotificationChime = async (): Promise<void> => {
  if (!isSoundEnabled()) return;
  const now = Date.now();
  if (now - lastPlayedAt < THROTTLE_MS) return;
  lastPlayedAt = now;

  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      return;
    }
  }

  const start = ctx.currentTime;
  playTone(ctx, 988, start, 0.12);
  playTone(ctx, 740, start + 0.09, 0.18);
};

const playTone = (ctx: AudioContext, freq: number, startAt: number, durationS: number): void => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, startAt);
  // Envelope: quick attack + smooth exponential decay. The
  // exponentialRampToValueAtTime can't target 0, so we end at a
  // tiny non-zero value.
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(0.18, startAt + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + durationS);
  osc.connect(gain).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + durationS + 0.02);
};

// ============================================================================
// User preference (localStorage)
// ============================================================================

export const isSoundEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  const v = window.localStorage.getItem(STORAGE_KEY);
  // Default: enabled. Operators can opt out via setSoundEnabled(false).
  return v === null ? true : v === '1';
};

export const setSoundEnabled = (enabled: boolean): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
};

/**
 * Warm up audio on an explicit user gesture (bell click, sound toggle).
 * Do not create or resume an AudioContext from passive effects - browsers
 * block that and log autoplay warnings.
 */
export const warmUpAudio = (): void => {
  const ctx = getCtx();
  if (!ctx || ctx.state !== 'suspended') return;
  void ctx.resume().catch(() => {
    /* Autoplay policy - resume on next explicit click via playNotificationChime. */
  });
};
